import { FadeIn } from "./FadeIn";

const STEPS = [
  { num: "01", title: "Discover", desc: "We sit with the business and understand how it genuinely operates." },
  { num: "02", title: "Map", desc: "We map the workflow from the first customer interaction through quoting, scheduling, operations, invoicing and completion." },
  { num: "03", title: "Identify", desc: "We identify bottlenecks, duplicated administration, unnecessary communication and areas where management is doing work technology could handle." },
  { num: "04", title: "Build", desc: "We design and develop the system around the actual business instead of forcing the business to adapt to generic software." },
  { num: "05", title: "Improve", desc: "The system can be expanded progressively as the business grows and new opportunities for automation become commercially worthwhile." },
];

const STAGES = [
  { stage: "Stage 1", title: "Digital presence, customer enquiries and quoting" },
  { stage: "Stage 2", title: "Job management, workflow tracking and customer communication" },
  { stage: "Stage 3", title: "Stock, materials and supplier purchasing" },
  { stage: "Stage 4", title: "Employees, timesheets, reporting and software integrations" },
  { stage: "Stage 5", title: "Advanced contract management, operational intelligence and profitability reporting" },
];

export const Process = () => (
  <section id="how-it-works" className="bg-offwhite border-b border-gray-200 scroll-mt-20" data-testid="how-it-works-section">
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-24 lg:py-32">
      <FadeIn className="max-w-3xl">
        <p className="text-[11px] font-semibold tracking-[0.28em] text-forest uppercase mb-5">How It Works</p>
        <h2 className="font-heading font-bold tracking-tight text-charcoal text-2xl sm:text-3xl lg:text-4xl leading-tight" data-testid="how-it-works-heading">
          A considered process, from first conversation to working system.
        </h2>
      </FadeIn>
      <div className="mt-14 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-7">
          {STEPS.map((s, i) => (
            <FadeIn key={s.num} delay={i * 0.06}>
              <div className="flex gap-6 pb-10 last:pb-0 relative">
                {i < STEPS.length - 1 && <span className="absolute left-[27px] top-14 bottom-0 w-px bg-gray-300" />}
                <div className="shrink-0 w-14 h-14 rounded-md bg-white border border-gray-200 flex items-center justify-center font-heading font-bold text-forest text-sm tracking-wider relative z-10">
                  {s.num}
                </div>
                <div className="pt-2">
                  <h3 className="font-heading font-bold text-charcoal text-lg tracking-wide uppercase">{s.title}</h3>
                  <p className="mt-2 text-sm md:text-base text-zinc-600 leading-relaxed max-w-xl">{s.desc}</p>
                </div>
              </div>
            </FadeIn>
          ))}
        </div>
        <div className="lg:col-span-5">
          <FadeIn delay={0.15} className="lg:sticky lg:top-28">
            <div className="bg-forest rounded-lg p-8 lg:p-10" data-testid="process-quote">
              <svg width="34" height="26" viewBox="0 0 34 26" fill="none" aria-hidden="true">
                <path d="M0 26V14.4C0 6.4 4.8 1.1 13.2 0l1.6 4.4c-4.6 1.3-6.8 4-7.2 7.6H13V26H0Zm21 0V14.4C21 6.4 25.8 1.1 34.2 0l1.6 4.4c-4.6 1.3-6.8 4-7.2 7.6H34V26H21Z" fill="#739072" transform="scale(0.9)"/>
              </svg>
              <p className="mt-6 font-heading font-semibold text-white text-xl lg:text-2xl leading-snug">
                Your business should not have to change the way it works to suit software. The software should be
                built around the way your business works.
              </p>
            </div>
          </FadeIn>
        </div>
      </div>
    </div>
  </section>
);

export const StagedImplementation = () => (
  <section className="bg-white border-b border-gray-200" data-testid="staged-implementation-section">
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-24 lg:py-32">
      <FadeIn className="max-w-3xl">
        <p className="text-[11px] font-semibold tracking-[0.28em] text-forest uppercase mb-5">Staged Implementation</p>
        <h2 className="font-heading font-bold tracking-tight text-charcoal text-2xl sm:text-3xl lg:text-4xl leading-tight" data-testid="staged-heading">
          Modernisation without replacing everything overnight.
        </h2>
        <p className="mt-5 text-base md:text-lg text-zinc-600 leading-relaxed">
          Westline Technologies prefers staged implementation rather than attempting to replace every existing process
          at once. An example progression:
        </p>
      </FadeIn>
      <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-5 gap-px bg-gray-200 border border-gray-200" data-testid="stages-grid">
        {STAGES.map((s, i) => (
          <FadeIn key={s.stage} delay={i * 0.07} className="bg-white">
            <div className="p-6 h-full flex flex-col">
              <span className="font-heading font-bold text-sage text-xs tracking-[0.2em] uppercase">{s.stage}</span>
              <span className="mt-1 font-heading font-extrabold text-charcoal/10 text-5xl leading-none select-none">{i + 1}</span>
              <p className="mt-3 text-sm font-medium text-charcoal leading-relaxed">{s.title}</p>
            </div>
          </FadeIn>
        ))}
      </div>
      <FadeIn className="mt-10 max-w-3xl">
        <p className="text-sm md:text-base text-zinc-600 leading-relaxed" data-testid="staged-note">
          The exact stages depend on the business. We first identify which changes are likely to produce the
          greatest commercial value.
        </p>
      </FadeIn>
    </div>
  </section>
);
